import { useState } from "react";

import { Prisma } from "@prisma/client";
import { SearchUser } from "~/components/v2/users/SearchUser";
import { CommunityPage } from "~/types/CommunityTypes";
import { api } from "~/utils/api";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import { Button } from "../../themed/Button";
import UserLink from "../../users-components/UserLink";
import { Switch } from "../../themed/Switch";
import { Label } from "~/components/shadcn/Label";
import { toastHandler } from "~/components/ToastHandler";

interface TopHostsProps {
    community: CommunityPage;
    refetch?: () => void;
}

const EditTopHosts: React.FC<TopHostsProps> = ({ community, refetch }) => {
    const [selectedTopHost, setSelectTopHost] = useState<Prisma.UserGetPayload<{
        select: {
            id: true;
            name: true;
            email: true;
        };
    }> | null>(null);
    const [isTopHostsUsingLeaderboard, setIsTopHostsUsingLeaderboard] =
        useState(community.isTopHostsUsingLeaderboard);
    const router = useRouter();

    const [query, setQuery] = useState<string>("");
    const { data: users } = api.user.searchUsers.useQuery(
        {
            slug: community?.slug,
            searchQuery: query,
        },
        {
            enabled: !!community,
        },
    );

    const handleTopHostLeaderboard = async (isUsingLeaderboard: boolean) => {
        setIsTopHostsUsingLeaderboard(isUsingLeaderboard);
        await toastHandler({
            loadingMessage: "Updating Top Hosts setting",
            successMessage: isUsingLeaderboard
                ? "Top Hosts is now using leaderboard"
                : "Top Hosts in now editorialized",
            errorMessage: `Failed to update Top Hosts setting`,
            operation: async () => {
                await updateCommunity.mutateAsync({
                    slug: community.slug,
                    isTopHostsUsingLeaderboard: isUsingLeaderboard,
                });
            },
        });
    };

    const updateCommunity = api.community.updateCommunity.useMutation();

    const handleEditTopHost = (emailToDete?: string | null) => {
        if (!community) return;
        const onSuccess = {
            onSuccess: () => {
                setSelectTopHost(null);
                refetch?.();
                router.refresh();
                toast.success("Top host updated successfully");
            },
        };
        if (emailToDete) {
            updateCommunity.mutateAsync(
                {
                    slug: community.slug,
                    topHost: {
                        email: emailToDete,
                        remove: true,
                    },
                },
                onSuccess,
            );
        }
        if (selectedTopHost && selectedTopHost.email) {
            updateCommunity.mutateAsync(
                {
                    slug: community.slug,
                    topHost: {
                        email: selectedTopHost.email,
                    },
                },
                onSuccess,
            );
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <h6>Top Hosts</h6>
            <div className="mb-2 flex text-v3-N-300">
                <Label className="grow">Use Leaderboard</Label>
                <Switch
                    size="sm"
                    checked={isTopHostsUsingLeaderboard ?? false}
                    onClick={() =>
                        handleTopHostLeaderboard(!isTopHostsUsingLeaderboard)
                    }
                />
            </div>
            {community?.topHosts?.map((h) => (
                <div
                    className="flex w-full max-w-[350px] gap-4"
                    key={`organizer-host-${h.id}`}
                >
                    <UserLink user={h} />
                    <Button
                        className="w-[100px] rounded"
                        variant="destructive"
                        onClick={() => handleEditTopHost(h.email)}
                    >
                        Remove
                    </Button>
                </div>
            ))}
            <div className="mt-4 flex w-full max-w-[350px] flex-col gap-4">
                <SearchUser
                    users={users}
                    placeholder="Search for a person"
                    setQuery={setQuery}
                    selected={selectedTopHost}
                    setSelected={setSelectTopHost}
                />
                <Button className="rounded" onClick={() => handleEditTopHost()}>
                    Add
                </Button>
            </div>
        </div>
    );
};

export default EditTopHosts;
